import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Package, ShoppingBag, XCircle } from 'lucide-react';
import './styles/customer-orders.css';

const FILTERS = [
  { key: 'all',        label: 'All' },
  { key: 'pending',    label: 'Pending' },
  { key: 'processing', label: 'Processing' },
  { key: 'shipped',    label: 'Shipped' },
  { key: 'delivered',  label: 'Delivered' },
  { key: 'cancelled',  label: 'Cancelled' },
];

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-NG', {
    year: 'numeric', month: 'short', day: 'numeric'
  });
};

const formatPrice = (value) => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency', currency: 'NGN', minimumFractionDigits: 2, maximumFractionDigits: 2
  }).format(parseFloat(value) || 0);
};

const getOrderNumber = (order) => {
  if (order.orderNumber) return order.orderNumber;
  return order.id ? order.id.slice(-8).toUpperCase() : 'N/A';
};

const pillClass = (status) => {
  const key = (status || 'pending').toLowerCase();
  return `cord-pill cord-pill--${key}`;
};

const CustomerOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  const fetchOrders = useCallback(async () => {
    try {
      setError('');
      const response = await fetch('/api/customer/order-tracking', {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      } else {
        throw new Error('Failed to fetch orders');
      }
    } catch (err) {
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const filteredOrders = useMemo(() => {
    if (filter === 'all') return orders;
    return orders.filter(order => (order.status || '').toLowerCase() === filter);
  }, [orders, filter]);

  const goToOrder = (orderId) => {
    if (!orderId) return;
    navigate(`/dashboard/orders/${orderId}`);
  };

  return (
    <div className="cord">
      <header className="cord__head">
        <h1 className="cord__title">My orders</h1>
        <p className="cord__sub">Track and manage your purchases.</p>
      </header>

      <nav className="cord__tabs" aria-label="Filter orders by status">
        {FILTERS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`cord-tab ${filter === key ? 'is-active' : ''}`}
            onClick={() => setFilter(key)}
            aria-pressed={filter === key}
          >
            {label}
          </button>
        ))}
      </nav>

      {error && (
        <div className="cord__alert" role="alert">
          <XCircle size={16} aria-hidden />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="cord__card">
          <ul className="cord-list">
            {[0, 1, 2, 3].map(i => (
              <li key={i} className="cord-row cord-row--skel">
                <div className="cord__skel cord__skel--line" />
                <div className="cord__skel cord__skel--pill" />
                <div className="cord__skel cord__skel--total" />
              </li>
            ))}
          </ul>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="cord__empty">
          <Package size={32} aria-hidden />
          <div>
            <div className="cord__empty-title">No orders {filter !== 'all' ? `in ${filter}` : 'yet'}</div>
            <div className="cord__empty-sub">
              {filter === 'all'
                ? "Once you place an order it'll show up here."
                : 'Try a different filter or place a new order.'}
            </div>
          </div>
          <button type="button" className="cord__empty-btn" onClick={() => navigate('/dashboard')}>
            <ShoppingBag size={14} /> Back to dashboard
          </button>
        </div>
      ) : (
        <div className="cord__card">
          <ul className="cord-list">
            {filteredOrders.map((order) => {
              const itemCount = (order.orderItems || order.items || []).length;
              const storeName = order.store?.name || order.merchant?.username || '';
              return (
                <li
                  key={order.id}
                  className="cord-row"
                  onClick={() => goToOrder(order.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      goToOrder(order.id);
                    }
                  }}
                >
                  <div className="cord-row__primary">
                    <div className="cord-row__id">#{getOrderNumber(order)}</div>
                    <div className="cord-row__meta">
                      <span>{formatDate(order.createdAt)}</span>
                      {itemCount > 0 && (
                        <>
                          <span className="cord-row__dot" aria-hidden>·</span>
                          <span>{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
                        </>
                      )}
                      {storeName && (
                        <>
                          <span className="cord-row__dot" aria-hidden>·</span>
                          <span className="cord-row__store">{storeName}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <span className={pillClass(order.status)}>{order.status || 'Pending'}</span>
                  <div className="cord-row__total">{formatPrice(order.total)}</div>
                  <Eye size={16} className="cord-row__eye" aria-hidden />
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CustomerOrders;
